import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Label,
  FormFeedback,
  Button,
  Card,
  CardBody,
  CardTitle,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  ModalFooter,
  Spinner,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import { useFormik } from "formik";
import CustomerServices from "../../../services/OrderingServices/CustomerServices";
import toast from "react-hot-toast";
import CustomerTypeService from "../../../services/OrderingServices/CustomerTypeService";
import Select from "react-select";
import CostCenterService from "../../../services/OrderingServices/CostCenterService";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

// ENUM Mapping (if needed elsewhere, but not used in payload)
const PrinterTypeEnum = {
  Local: 1,
  Network: 2,
  Dummy: 3,
};

const printerTypeOptions = [
  { value: 1, label: "Local" },
  { value: 2, label: "Network" },
  { value: 3, label: "Dummy" },
];

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  description: Yup.string(),
  printerName: Yup.string().required("Printer name is required"),
  printerIpAddress: Yup.string(),
  printerPort: Yup.string(),
  printerType: Yup.number().required("Printer type is required"),
});

const CostCenterForm = ({
  selectedItem,
  modal,
  toggleModal,
  fetchCostCenter,
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: selectedItem?.id || 0,
      name: selectedItem?.name || "",
      description: selectedItem?.description || "",
      isActive: selectedItem?.isActive ?? true,
      printerName: selectedItem?.printerName || "",
      printerIpAddress: selectedItem?.printerIpAddress || "",
      printerPort: selectedItem?.printerPort || "",
      printerType: selectedItem?.printerType
        ? PrinterTypeEnum[selectedItem.printerType] || null
        : null,
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Directly use numeric value
      const payload = {
        ...values,
        printerType: values.printerType, // already numeric
      };

      try {
        setLoading(true);
        if (values.id) {
          await CostCenterService.update(values.id, payload);
          toast.success("Printer updated successfully.");
        } else {
          await CostCenterService.create(payload);
          toast.success("New printer is added successfully");
          resetForm();
        }

        fetchCostCenter();
        toggleModal();
      } catch (error) {
        console.error(error);
        toast.error("Error while submitting form");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!modal) {
      formik.resetForm();
    }
  }, [modal]);

  const handlePrinterTypeChange = (selectedOption) => {
    formik.setFieldValue(
      "printerType",
      selectedOption ? selectedOption.value : null
    );
  };

  const selectedPrinterType = printerTypeOptions.find(
    (option) => option.value === formik.values.printerType
  );
  return (
    <Offcanvas
      isOpen={modal}
      toggle={toggleModal}
      backdropClassName="bg-dark bg-opacity-50"
      direction="end"
      className="shadow-lg animate__animated animate__slideInRight bg-white "
    >
      <OffcanvasHeader toggle={toggleModal}>
        {selectedItem?.id ? "Update" : "Add"} Cost Center
      </OffcanvasHeader>
      <OffcanvasBody>
        <Form onSubmit={formik.handleSubmit}>
          <Row >
            <Col md={12} lg={12}>
              <div className="form-floating mb-3">
                <Input
                  id="name"
                  name="name"
                  type="text"
                  className="form-control"
                  placeholder="Enter name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  invalid={!!(formik.touched.name && formik.errors.name)}
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="name">Name</Label>
                {formik.touched.name && formik.errors.name && (
                  <FormFeedback type="invalid">
                    {formik.errors.name}
                  </FormFeedback>
                )}
              </div>
            </Col>
            <Col md={12} lg={12}>
              <div className="form-floating mb-3">
                <Input
                  id="description"
                  name="description"
                  type="text"
                  className="form-control"
                  placeholder="Enter description"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.description}
                  invalid={
                    !!(formik.touched.description && formik.errors.description)
                  }
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="description">Description</Label>
                {formik.touched.description && formik.errors.description && (
                  <FormFeedback type="invalid">
                    {formik.errors.description}
                  </FormFeedback>
                )}
              </div>
            </Col>
            <Col md={12} lg={12}>
              <div className="form-floating mb-3">
                <Input
                  id="printerName"
                  name="printerName"
                  type="text"
                  className="form-control"
                  placeholder="Enter printer name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.printerName}
                  invalid={
                    !!(formik.touched.printerName && formik.errors.printerName)
                  }
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="printerName">Printer Name</Label>
                {formik.touched.printerName && formik.errors.printerName && (
                  <FormFeedback type="invalid">
                    {formik.errors.printerName}
                  </FormFeedback>
                )}
              </div>
            </Col>
            <Col md={12} lg={12}>
              <div className="form-floating mb-3">
                <Input
                  id="printerIpAddress"
                  name="printerIpAddress"
                  type="text"
                  className="form-control"
                  placeholder="Enter IP address"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.printerIpAddress}
                  invalid={
                    !!(
                      formik.touched.printerIpAddress &&
                      formik.errors.printerIpAddress
                    )
                  }
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="printerIpAddress">Printer IP Address</Label>
                {formik.touched.printerIpAddress &&
                  formik.errors.printerIpAddress && (
                    <FormFeedback type="invalid">
                      {formik.errors.printerIpAddress}
                    </FormFeedback>
                  )}
              </div>
            

           
            

            </Col>
            <Col md={12} lg={12}>
              <div className="form-floating mb-3">
                <Input
                  id="printerPort"
                  name="printerPort"
                  type="text"
                  className="form-control"
                  placeholder="Enter printer port"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.printerPort}
                  invalid={
                    !!(formik.touched.printerPort && formik.errors.printerPort)
                  }
                  style={{ padding: "0.5rem", height: "1rem" }}
                />
                <Label for="printerPort">Printer Port</Label>
                {formik.touched.printerPort && formik.errors.printerPort && (
                  <FormFeedback type="invalid">
                    {formik.errors.printerPort}
                  </FormFeedback>
                )}
              </div>           
              <div className="mb-3">
                <Select
                  id="printerType"
                  name="printerType"
                  options={printerTypeOptions}
                  value={selectedPrinterType || null}
                  onChange={handlePrinterTypeChange}
                  placeholder="Select Printer Type"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (baseStyles) => ({
                      ...baseStyles,
                      padding: "0.3rem",
                    }),
                  }}
                />
                {formik.touched.printerType && formik.errors.printerType && (
                  <div className="text-danger">{formik.errors.printerType}</div>
                )}
              </div>
            </Col>
            <Col md={12} lg={12}>
              <div className="form-check mt-2 ms-3">
                <Input
                  className="form-check-input"
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formik.values.isActive}
                  onChange={formik.handleChange}
                />
                <Label className="form-check-label " for="isActive">
                  Active
                </Label>
              </div>
            </Col>
          </Row>

          <div className="d-flex bg-white align-content-center align-items-center justify-content-between gap-2 position-absolute bottom-0 start-0 end-0 p-3 ">
            <button className="btn btn-danger w-100 " onClick={toggleModal}>
              Cancel
            </button>

            <Button
              color="success"
              type="submit"
              disabled={loading}
              className="w-100"
            >
              {loading && <Spinner size="sm" className="me-2" />}
              {loading
                ? selectedItem?.id
                  ? "Updating..."
                  : "Creating..."
                : selectedItem?.id
                ? "Update"
                : "Create"}
            </Button>
          </div>
        </Form>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default CostCenterForm;
